import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, MapPin, Trash2, Edit, ChevronDown, ChevronRight, Upload, Download, Mail, Send, FileText, Image, Check, Square, CheckSquare, Megaphone, MegaphoneOff, Eye, EyeOff, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Currency Display Component - moved outside to prevent re-creation on every render
const CurrencyDisplay = ({ amount, currency, showConversion = true, baseCurrency, getCurrencySymbol, convertCurrency }) => {
  const [convertedAmount, setConvertedAmount] = React.useState(null);
  
  React.useEffect(() => {
    if (showConversion && currency !== baseCurrency && amount) {
      convertCurrency(amount, currency, baseCurrency).then(converted => {
        setConvertedAmount(converted);
      });
    }
  }, [amount, currency, showConversion, baseCurrency, convertCurrency]);
  
  if (!amount) return null;
  
  const mainDisplay = `${getCurrencySymbol(currency)}${parseFloat(amount).toFixed(2)}`;
  
  if (!showConversion || currency === baseCurrency || !convertedAmount) {
    return <span>{mainDisplay}</span>;
  }
  
  return (
    <span>
      {mainDisplay}
      <span className="text-xs text-muted-foreground ml-1">
        ({getCurrencySymbol(baseCurrency)}{convertedAmount.toFixed(2)})
      </span>
    </span>
  );
};

// Import Shows Component with tabs for Image and Text - Now with duplicate detection
const ImportShowsContent = ({ selectedArtist, importingImage, setImportingImage, setImportDialogOpen, fetchShows }) => {
  const [activeTab, setActiveTab] = useState('image'); // 'image' or 'text'
  const [textContent, setTextContent] = useState('');
  const [tourName, setTourName] = useState('');
  const [performanceType, setPerformanceType] = useState('solo_live');
  const [showType, setShowType] = useState('venue');
  
  // Preview state for duplicate detection
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedShows, setSelectedShows] = useState(new Set()); // Indices of shows to import
  const [replaceDuplicates, setReplaceDuplicates] = useState(new Set()); // Existing show IDs to replace

  // Reset preview when switching tabs
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setShowPreview(false);
    setPreviewData(null);
    setSelectedShows(new Set());
    setReplaceDuplicates(new Set());
  };

  // Preview handler for Image import
  const handleImagePreview = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('import-image-input');
    const file = fileInput?.files[0];
    if (!file) {
      toast.error('Please select an image');
      return;
    }

    if (!selectedArtist || selectedArtist === 'all') {
      toast.error('Please select a specific artist first');
      return;
    }

    setImportingImage('Analyzing image for shows...');
    toast.info('Extracting show data...');

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/live-shows/import-preview`,
          {
            source_type: 'image',
            image_data: event.target.result,
            artist_id: selectedArtist,
            tour_name: tourName,
            performance_type: performanceType,
            show_type: showType
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setPreviewData(response.data);
        // Auto-select all new (non-duplicate) shows
        const newIndices = new Set();
        response.data.extracted?.forEach((show, idx) => {
          if (!show.is_duplicate) {
            newIndices.add(idx);
          }
        });
        setSelectedShows(newIndices);
        setReplaceDuplicates(new Set());
        setShowPreview(true);
        
        if (response.data.duplicate_count > 0) {
          toast.warning(`Found ${response.data.duplicate_count} duplicate date(s). Review below.`);
        } else {
          toast.success(`Found ${response.data.total_extracted} show(s). Review and confirm.`);
        }
        setImportingImage(null);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to extract shows');
        setImportingImage(null);
      }
    };
    reader.readAsDataURL(file);
  };

  // Preview handler for Text import
  const handleTextPreview = async (e) => {
    e.preventDefault();
    
    if (!textContent.trim()) {
      toast.error('Please paste some text content');
      return;
    }

    if (!selectedArtist || selectedArtist === 'all') {
      toast.error('Please select a specific artist first');
      return;
    }

    setImportingImage('Analyzing text for shows...');
    toast.info('Extracting show data...');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/live-shows/import-preview`,
        {
          source_type: 'text',
          text_content: textContent,
          artist_id: selectedArtist,
          tour_name: tourName,
          performance_type: performanceType,
          show_type: showType
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPreviewData(response.data);
      // Auto-select all new (non-duplicate) shows
      const newIndices = new Set();
      response.data.extracted?.forEach((show, idx) => {
        if (!show.is_duplicate) {
          newIndices.add(idx);
        }
      });
      setSelectedShows(newIndices);
      setReplaceDuplicates(new Set());
      setShowPreview(true);
      
      if (response.data.duplicate_count > 0) {
        toast.warning(`Found ${response.data.duplicate_count} duplicate date(s). Review below.`);
      } else {
        toast.success(`Found ${response.data.total_extracted} show(s). Review and confirm.`);
      }
      setImportingImage(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract shows from text');
      setImportingImage(null);
    }
  };

  // Toggle show selection
  const toggleShowSelection = (idx, show) => {
    const newSelected = new Set(selectedShows);
    const newReplace = new Set(replaceDuplicates);
    
    if (newSelected.has(idx)) {
      newSelected.delete(idx);
      // Also remove from replace list if it was a duplicate
      if (show.is_duplicate && show.existing_show?.id) {
        newReplace.delete(show.existing_show.id);
      }
    } else {
      newSelected.add(idx);
      // If duplicate, also mark existing for replacement
      if (show.is_duplicate && show.existing_show?.id) {
        newReplace.add(show.existing_show.id);
      }
    }
    
    setSelectedShows(newSelected);
    setReplaceDuplicates(newReplace);
  };

  // Select all shows
  const selectAll = () => {
    const newSelected = new Set();
    const newReplace = new Set();
    previewData?.extracted?.forEach((show, idx) => {
      newSelected.add(idx);
      if (show.is_duplicate && show.existing_show?.id) {
        newReplace.add(show.existing_show.id);
      }
    });
    setSelectedShows(newSelected);
    setReplaceDuplicates(newReplace);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedShows(new Set());
    setReplaceDuplicates(new Set());
  };

  // Confirm and import selected shows
  const handleConfirmImport = async () => {
    if (selectedShows.size === 0) {
      toast.error('Please select at least one show to import');
      return;
    }

    setImportingImage('Importing selected shows...');

    try {
      const token = localStorage.getItem('token');
      const showsToCreate = Array.from(selectedShows).map(idx => previewData.extracted[idx]);
      
      const response = await axios.post(
        `${API}/live-shows/import-confirm`,
        {
          shows: showsToCreate,
          artist_id: selectedArtist,
          replace_duplicates: Array.from(replaceDuplicates)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setImportDialogOpen(false);
      setImportingImage(null);
      setShowPreview(false);
      setPreviewData(null);
      setSelectedShows(new Set());
      setReplaceDuplicates(new Set());
      setTextContent('');
      fetchShows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import shows');
      setImportingImage(null);
    }
  };

  // Go back to input form
  const handleBackToInput = () => {
    setShowPreview(false);
    setPreviewData(null);
    setSelectedShows(new Set());
    setReplaceDuplicates(new Set());
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Preview View
  if (showPreview && previewData) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">Review Extracted Shows</h3>
          <Button variant="outline" size="sm" onClick={handleBackToInput}>
            Back
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{previewData.total_extracted || 0}</p>
            <p className="text-xs text-muted-foreground">Total Found</p>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{previewData.new_count || 0}</p>
            <p className="text-xs text-muted-foreground">New Shows</p>
          </div>
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-orange-600">{previewData.duplicate_count || 0}</p>
            <p className="text-xs text-muted-foreground">Duplicates</p>
          </div>
        </div>

        {/* Selection buttons */}
        <div className="flex gap-2 mb-4">
          <Button variant="outline" size="sm" onClick={selectAll}>
            <CheckSquare className="w-4 h-4 mr-1" />
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={clearSelection}>
            <Square className="w-4 h-4 mr-1" />
            Clear
          </Button>
          <span className="text-sm text-muted-foreground ml-auto self-center">
            {selectedShows.size} selected
          </span>
        </div>

        {/* Shows list */}
        <div className="space-y-2 max-h-[300px] overflow-y-auto mb-4">
          {previewData.extracted?.map((show, idx) => (
            <div
              key={`${show.date}-${show.venue}-${idx}`}
              onClick={() => toggleShowSelection(idx, show)}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                selectedShows.has(idx)
                  ? show.is_duplicate
                    ? 'bg-orange-500/20 border-orange-500/50'
                    : 'bg-green-500/20 border-green-500/50'
                  : 'bg-card/50 border-border hover:border-primary/30'
              }`}
              data-testid={`preview-show-${idx}`}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <div className="mt-1">
                  {selectedShows.has(idx) ? (
                    <CheckSquare className="w-5 h-5 text-primary" />
                  ) : (
                    <Square className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                
                {/* Show details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{formatDate(show.date)}</p>
                    {show.is_duplicate && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-orange-500/20 text-orange-600 rounded-full">
                        DUPLICATE
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {show.venue}, {show.city}, {show.country}
                  </p>
                  {show.notes && (
                    <p className="text-xs text-muted-foreground/70 mt-1">{show.notes}</p>
                  )}
                  
                  {/* Show existing show info if duplicate */}
                  {show.is_duplicate && show.existing_show && (
                    <div className="mt-2 pt-2 border-t border-border/50">
                      <p className="text-xs text-orange-600 font-medium">Existing show on this date:</p>
                      <p className="text-xs text-muted-foreground">
                        {show.existing_show.venue}, {show.existing_show.city}
                      </p>
                      {selectedShows.has(idx) && (
                        <p className="text-xs text-orange-500 mt-1 font-medium">
                          Will replace existing show
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No shows found message */}
        {(!previewData.extracted || previewData.extracted.length === 0) && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No shows found in the provided content.</p>
            <Button variant="outline" onClick={handleBackToInput} className="mt-4">
              Try Again
            </Button>
          </div>
        )}

        {/* Confirm button */}
        {previewData.extracted?.length > 0 && (
          <Button
            onClick={handleConfirmImport}
            disabled={!!importingImage || selectedShows.size === 0}
            className="w-full bg-primary hover:bg-primary/90"
            data-testid="confirm-import-btn"
          >
            {importingImage ? (
              'Importing...'
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Confirm & Import {selectedShows.size} Show{selectedShows.size !== 1 ? 's' : ''}
                {replaceDuplicates.size > 0 && ` (replacing ${replaceDuplicates.size} duplicate${replaceDuplicates.size !== 1 ? 's' : ''})`}
              </>
            )}
          </Button>
        )}
      </div>
    );
  }

  // Input Form View
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Tab Buttons */}
      <div className="flex gap-2 mb-4">
        <Button
          type="button"
          variant={activeTab === 'image' ? 'default' : 'outline'}
          onClick={() => handleTabSwitch('image')}
          className="flex-1"
        >
          <Image className="w-4 h-4 mr-2" />
          From Image
        </Button>
        <Button
          type="button"
          variant={activeTab === 'text' ? 'default' : 'outline'}
          onClick={() => handleTabSwitch('text')}
          className="flex-1"
        >
          <FileText className="w-4 h-4 mr-2" />
          Paste Text
        </Button>
      </div>

      {/* Artist Warning */}
      {(!selectedArtist || selectedArtist === 'all') && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4">
          <p className="text-sm text-orange-600 font-semibold">Please select a specific artist first</p>
        </div>
      )}

      {/* Tour Name (shared between tabs) */}
      <div className="space-y-2 mb-4">
        <Label htmlFor="import-tour-name">Tour Name (Optional)</Label>
        <Input
          id="import-tour-name"
          value={tourName}
          onChange={(e) => setTourName(e.target.value)}
          placeholder="e.g., Spring 2025 Tour"
          className="bg-input/50 border-border rounded-xl"
        />
      </div>

      {/* Show Type & Performance Type Selection */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="space-y-2">
          <Label>Show Type</Label>
          <select
            value={showType}
            onChange={(e) => setShowType(e.target.value)}
            className="w-full h-10 px-3 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
            data-testid="import-show-type"
          >
            <option value="venue">Venue</option>
            <option value="festival">Festival</option>
            <option value="show">Show</option>
            <option value="tour">Tour</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label>Performance Type</Label>
          <select
            value={performanceType}
            onChange={(e) => setPerformanceType(e.target.value)}
            className="w-full h-10 px-3 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
            data-testid="import-performance-type"
          >
            <option value="solo_live">Solo Live</option>
            <option value="live_band">Live Band</option>
            <option value="dj">DJ Set</option>
          </select>
        </div>
      </div>

      {/* Image Tab Content */}
      {activeTab === 'image' && (
        <form onSubmit={handleImagePreview} className="space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="font-semibold text-blue-600 mb-2">Import from Image</h4>
            <p className="text-sm text-muted-foreground">
              Upload a photo of your tour sheet. AI will extract shows and check for duplicates.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="import-image-input">Upload Image</Label>
            <Input
              id="import-image-input"
              type="file"
              accept="image/*,.pdf"
              className="bg-input/50 border-border rounded-xl"
              data-testid="import-image-input"
            />
            <p className="text-xs text-muted-foreground">
              Accepts photos, screenshots, or PDF documents
            </p>
          </div>

          {importingImage && (
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
              <p className="text-sm text-primary animate-pulse">{importingImage}</p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90"
            disabled={!!importingImage || !selectedArtist || selectedArtist === 'all'}
            data-testid="extract-image-btn"
          >
            {importingImage ? 'Processing...' : 'Extract & Preview Shows'}
          </Button>
        </form>
      )}

      {/* Text Tab Content */}
      {activeTab === 'text' && (
        <form onSubmit={handleTextPreview} className="space-y-4">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <h4 className="font-semibold text-green-600 mb-2">Import from Email/Text</h4>
            <p className="text-sm text-muted-foreground">
              Paste booking confirmation emails. AI will extract shows and check for duplicates.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="import-text-content">Paste Email or Text</Label>
            <Textarea
              id="import-text-content"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Paste booking confirmation email here...

Example:
Hi, we're pleased to confirm your booking:

Date: 15th March 2025
Venue: The Fleece
City: Bristol
Fee: £500

Please confirm your acceptance..."
              className="bg-input/50 border-border rounded-xl min-h-[200px] font-mono text-sm"
              data-testid="import-text-input"
            />
            <p className="text-xs text-muted-foreground">
              Works with booking emails, tour sheets, or any text with show details
            </p>
          </div>

          {importingImage && (
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
              <p className="text-sm text-primary animate-pulse">{importingImage}</p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90"
            disabled={!!importingImage || !selectedArtist || selectedArtist === 'all' || !textContent.trim()}
            data-testid="extract-text-btn"
          >
            {importingImage ? 'Processing...' : 'Extract & Preview Shows'}
          </Button>
        </form>
      )}
    </div>
  );
};

const LiveShows = ({ user, selectedArtist }) => {
  const [shows, setShows] = useState([]);
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingShow, setEditingShow] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    recipient_email: '',
    recipient_name: '',
    message: '',
  });
  const [emailHidePerformance, setEmailHidePerformance] = useState(false);
  const [emailHideTour, setEmailHideTour] = useState(false);
  const [emailIncludePreviousShows, setEmailIncludePreviousShows] = useState(false);
  const [emailPreviousShowsCount, setEmailPreviousShowsCount] = useState(10);
  // Selection mode for export/email
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedShows, setSelectedShows] = useState(new Set());
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  // Announced filter: 'all', 'announced', 'unannounced'
  const [announcedFilter, setAnnouncedFilter] = useState('all');
  const [formData, setFormData] = useState({
    venue: '',
    city: '',
    country: '',
    date: '',
    notes: '',
    artist_id: '',
    show_type: 'venue',
    performance_type: 'solo_live',
    fee: '',
    costs: '',
    currency: 'GBP',
    tour_name: '',
    announced: false,
    guestlist: '',
  });
  
  // Guestlist editing state
  const [editingGuestlist, setEditingGuestlist] = useState(null); // show ID being edited
  const [guestlistText, setGuestlistText] = useState('');
  
  // Performance types
  const PERFORMANCE_TYPES = [
    { value: 'solo_live', label: 'Solo Live' },
    { value: 'live_band', label: 'Live Band' },
    { value: 'dj', label: 'DJ Set' },
  ];
  
  const [baseCurrency, setBaseCurrency] = useState('GBP');
  const [exchangeRates, setExchangeRates] = useState({});
  
  // Supported currencies
  const CURRENCIES = [
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
    { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
    { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
    { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
    { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
    { code: 'RON', name: 'Romanian Leu', symbol: 'lei' },
    { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
    { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn' },
    { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
    { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
    { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  ];
  
  const getCurrencySymbol = (code) => {
    const currency = CURRENCIES.find(c => c.code === code);
    return currency ? currency.symbol : code;
  };
  
  const [groupByTour, setGroupByTour] = useState(true);
  const [expandedTours, setExpandedTours] = useState({});
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importingImage, setImportingImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showToDelete, setShowToDelete] = useState(null);

  // Separate shows into upcoming and past (with announced filter)
  const { upcomingShows, pastShows, announcedCount, unannouncedCount } = React.useMemo(() => {
    const now = new Date();
    
    // First apply announced filter
    let filteredShows = shows;
    if (announcedFilter === 'announced') {
      filteredShows = shows.filter(show => show.announced);
    } else if (announcedFilter === 'unannounced') {
      filteredShows = shows.filter(show => !show.announced);
    }
    
    const upcoming = filteredShows.filter(show => new Date(show.date) >= now);
    const past = filteredShows.filter(show => new Date(show.date) < now);
    
    // Sort upcoming shows by date ascending (soonest first)
    upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));
    // Sort past shows by date descending (most recent first)
    past.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Count announced/unannounced (from unfiltered shows)
    const announced = shows.filter(s => s.announced).length;
    const unannounced = shows.filter(s => !s.announced).length;
    
    return { 
      upcomingShows: upcoming, 
      pastShows: past,
      announcedCount: announced,
      unannouncedCount: unannounced
    };
  }, [shows, announcedFilter]);

  // Group shows by tour
  const groupedUpcoming = React.useMemo(() => {
    if (!groupByTour) return { 'All Shows': upcomingShows };
    
    const grouped = {};
    upcomingShows.forEach(show => {
      const tourKey = show.tour_name || 'No Tour';
      if (!grouped[tourKey]) {
        grouped[tourKey] = [];
      }
      grouped[tourKey].push(show);
    });
    return grouped;
  }, [upcomingShows, groupByTour]);

  const groupedPast = React.useMemo(() => {
    if (!groupByTour) return { 'All Shows': pastShows };
    
    const grouped = {};
    pastShows.forEach(show => {
      const tourKey = show.tour_name || 'No Tour';
      if (!grouped[tourKey]) {
        grouped[tourKey] = [];
      }
      grouped[tourKey].push(show);
    });
    return grouped;
  }, [pastShows, groupByTour]);

  // Calculate tour totals
  const getTourTotals = (tourShows) => {
    const totalFee = tourShows.reduce((sum, show) => sum + (show.fee || 0), 0);
    const totalCosts = tourShows.reduce((sum, show) => sum + (show.costs || 0) + (show.agent_commission || 0), 0);
    const netProfit = totalFee - totalCosts;
    return { totalFee, totalCosts, netProfit, showCount: tourShows.length };
  };

  // Calculate overall totals for ALL shows
  const overallTotals = {
    totalShows: shows.length,
    upcomingCount: upcomingShows.length,
    pastCount: pastShows.length,
    totalIncome: shows.reduce((sum, show) => sum + (show.fee || 0), 0),
    totalCosts: shows.reduce((sum, show) => sum + (show.costs || 0) + (show.agent_commission || 0), 0),
    get netProfit() { return this.totalIncome - this.totalCosts; }
  };

  const [existingTours, setExistingTours] = useState([]);

  const fetchShows = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = selectedArtist ? `?artist_id=${selectedArtist}` : '';
      const response = await axios.get(`${API}/live-shows${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShows(response.data);
      
      // Extract unique tour names for autocomplete
      const tours = [...new Set(response.data.map(s => s.tour_name).filter(Boolean))];
      setExistingTours(tours);
    } catch (error) {
      toast.error('Failed to fetch shows');
    }
  }, [selectedArtist]);

  const fetchArtists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      // Silent fail - artists list is supplementary
    }
  }, []);

  useEffect(() => {
    fetchShows();
    if (user?.role === 'manager' || user?.role === 'admin') {
      fetchArtists();
    }
    fetchBaseCurrency();
  }, [selectedArtist, fetchShows, fetchArtists, user?.role]);
  
  const fetchBaseCurrency = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.default_currency) {
        setBaseCurrency(response.data.default_currency);
      }
    } catch (error) {
      // Silent fail - use default GBP
    }
  };
  
  const convertCurrency = useCallback(async (amount, fromCurrency, toCurrency) => {
    if (!amount || fromCurrency === toCurrency) return amount;
    
    try {
      const cacheKey = `${fromCurrency}_${toCurrency}`;
      if (exchangeRates[cacheKey]) {
        return amount * exchangeRates[cacheKey];
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/exchange-rate/${fromCurrency}/${toCurrency}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const rate = response.data.rate;
      setExchangeRates(prev => ({ ...prev, [cacheKey]: rate }));
      return amount * rate;
    } catch (error) {
      
      return amount;
    }
  }, [exchangeRates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      // If receipts uploaded, add flag to process them
      const payload = {
        ...formData,
        artist_id: formData.artist_id || selectedArtist || null,
        date: new Date(formData.date).toISOString(),
        fee: formData.fee ? parseFloat(formData.fee) : null,
        costs: formData.costs ? parseFloat(formData.costs) : null,
        receipts: receiptFiles,
        process_receipts: receiptFiles.length > 0, // Flag to trigger AI processing
        announced: formData.announced || false,
        guestlist: formData.guestlist || '',
      };

      if (editingShow) {
        // Update existing show
        await axios.put(`${API}/live-shows/${editingShow.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Show updated successfully!');
      } else {
        // Create new show
        await axios.post(`${API}/live-shows`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Show added successfully!');
      }
      
      setOpen(false);
      setEditingShow(null);
      setFormData({ 
        venue: '', city: '', country: '', date: '', notes: '', artist_id: '',
        show_type: 'venue', performance_type: 'solo_live', fee: '', costs: '', currency: 'GBP', tour_name: '', announced: false, guestlist: ''
      });
      fetchShows();
    } catch (error) {
      toast.error(editingShow ? 'Failed to update show' : 'Failed to add show');
    }
  };

  const handleEdit = (show) => {
    setEditingShow(show);
    setFormData({
      venue: show.venue,
      city: show.city,
      country: show.country,
      date: new Date(show.date).toISOString().slice(0, 16), // Format for datetime-local input
      notes: show.notes || '',
      artist_id: show.artist_id || '',
      show_type: show.show_type || 'venue',
      performance_type: show.performance_type || 'solo_live',
      fee: show.fee ? show.fee.toString() : '',
      costs: show.costs ? show.costs.toString() : '',
      currency: show.currency || 'GBP',
      tour_name: show.tour_name || '',
      announced: show.announced || false,
      guestlist: show.guestlist || '',
    });
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    setEditingShow(null);
    setFormData({
      venue: '', city: '', country: '', date: '', notes: '',
      artist_id: '', show_type: 'venue', performance_type: 'solo_live', fee: '', costs: '', currency: 'GBP', tour_name: '', announced: false, guestlist: ''
    });
    setReceiptFiles([]);
  };

  const handleReceiptUpload = (e) => {
    const files = Array.from(e.target.files);
    const promises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve({
            filename: file.name,
            file_data: event.target.result.split(',')[1],
            file_type: file.type,
          });
        };
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(promises).then(uploadedFiles => {
      setReceiptFiles(prev => [...prev, ...uploadedFiles]);
    });
  };

  const removeReceipt = (index) => {
    setReceiptFiles(prev => prev.filter((_, i) => i !== index));
  };

  const confirmDeleteShow = (show) => {
    setShowToDelete(show);
    setDeleteDialogOpen(true);
  };

  const deleteShow = async () => {
    if (!showToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/live-shows/${showToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Show deleted!');
      setDeleteDialogOpen(false);
      setShowToDelete(null);
      await fetchShows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete show');
    }
  };

  const handleEmailShows = async (e) => {
    e.preventDefault();
    
    // Check if we have shows to email
    const hasSelectedShows = selectionMode && selectedShows.size > 0;
    if (!hasSelectedShows && upcomingShows.length === 0) {
      toast.error('No shows to email');
      return;
    }

    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        recipient_email: emailFormData.recipient_email,
        recipient_name: emailFormData.recipient_name,
        message: emailFormData.message,
        artist_id: selectedArtist,
        hide_performance_type: emailHidePerformance,
        hide_tour_name: emailHideTour,
        include_previous_shows: emailIncludePreviousShows,
        previous_shows_count: emailPreviousShowsCount,
      };

      // If in selection mode with shows selected, include show_ids
      if (hasSelectedShows) {
        payload.show_ids = Array.from(selectedShows);
        payload.include_past = true; // Include past shows if selected
      }

      await axios.post(
        `${API}/live-shows/email-schedule`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Show schedule emailed successfully!');
      setEmailDialogOpen(false);
      setEmailFormData({ recipient_email: '', recipient_name: '', message: '' });
      setEmailHidePerformance(false);
      setEmailHideTour(false);
      setEmailIncludePreviousShows(false);
      setEmailPreviousShowsCount(10);
      // Clear selection after sending
      if (selectionMode) {
        setSelectedShows(new Set());
        setSelectionMode(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Toggle announced status for a single show
  const toggleAnnounced = async (showId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/live-shows/${showId}/announced`,
        { announced: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setShows(prev => prev.map(show => 
        show.id === showId ? { ...show, announced: response.data.announced } : show
      ));
      
      toast.success(response.data.announced ? 'Show marked as announced' : 'Show marked as unannounced');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update show');
    }
  };

  // Bulk toggle announced for selected shows
  const bulkToggleAnnounced = async (announced) => {
    if (selectedShows.size === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/live-shows/bulk-announced`,
        { show_ids: Array.from(selectedShows), announced },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setShows(prev => prev.map(show => 
        selectedShows.has(show.id) ? { ...show, announced } : show
      ));
      
      toast.success(`${selectedShows.size} shows ${announced ? 'announced' : 'unannounced'}`);
      setSelectedShows(new Set());
      setSelectionMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shows');
    }
  };

  // Save guestlist for a show
  const saveGuestlist = async (showId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/live-shows/${showId}/guestlist`,
        { guestlist: guestlistText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setShows(prev => prev.map(show => 
        show.id === showId ? { ...show, guestlist: guestlistText } : show
      ));
      
      setEditingGuestlist(null);
      setGuestlistText('');
      toast.success('Guestlist saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save guestlist');
    }
  };

  // Start editing guestlist
  const startEditingGuestlist = (show) => {
    setEditingGuestlist(show.id);
    setGuestlistText(show.guestlist || '');
  };

  // Cancel editing guestlist
  const cancelEditingGuestlist = () => {
    setEditingGuestlist(null);
    setGuestlistText('');
  };

  // Count guests from guestlist text (count non-empty lines)
  const countGuests = (guestlist) => {
    if (!guestlist) return 0;
    return guestlist.split('\n').filter(line => line.trim()).length;
  };

  // Get artist name from ID
  const getArtistName = (artistId) => {
    if (!artistId) return null;
    const artist = artists.find(a => a.id === artistId);
    return artist?.name || null;
  };

  // Bulk update selected shows
  const bulkUpdateShows = async (updates) => {
    if (selectedShows.size === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/live-shows/bulk-update`,
        { show_ids: Array.from(selectedShows), updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setShows(prev => prev.map(show => 
        selectedShows.has(show.id) ? { ...show, ...updates } : show
      ));
      
      toast.success(`${response.data.modified_count} shows updated`);
      setSelectedShows(new Set());
      setSelectionMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shows');
    }
  };

  // Search functionality removed

  return (
    <div className="space-y-8" data-testid="live-shows-page">
      <div className="space-y-4">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">Live Shows</h1>
          <p className="text-muted-foreground text-base mt-2">Manage your tour dates and performances</p>
        </div>

        {/* Overall Touring Tally */}
        {shows.length > 0 && (
          <Card className="bg-gradient-to-r from-card to-card/80 border-border/50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <h3 className="font-manrope font-bold text-lg">Overall Touring Summary</h3>
                <span className="text-sm text-muted-foreground">({overallTotals.totalShows} shows)</span>
              </div>
              <div className="flex flex-wrap gap-6">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Upcoming</p>
                  <p className="font-bold text-lg text-primary">{overallTotals.upcomingCount}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Completed</p>
                  <p className="font-bold text-lg">{overallTotals.pastCount}</p>
                </div>
                <div className="text-center border-l border-border pl-6">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Income</p>
                  <p className="font-bold text-lg text-green-500">£{overallTotals.totalIncome.toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Costs</p>
                  <p className="font-bold text-lg text-red-500">£{overallTotals.totalCosts.toFixed(2)}</p>
                </div>
                <div className="text-center border-l border-border pl-6">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Net Profit</p>
                  <p className={`font-bold text-lg ${overallTotals.netProfit >= 0 ? 'text-blue-500' : 'text-orange-500'}`}>
                    £{overallTotals.netProfit.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}
        
        {/* Action Buttons Row */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Selection Toggle */}
          <Button
            variant={selectionMode ? "default" : "outline"}
            onClick={() => {
              setSelectionMode(!selectionMode);
              if (selectionMode) {
                setSelectedShows(new Set()); // Clear selection when exiting
              }
            }}
            className={selectionMode ? "bg-primary" : ""}
          >
            {selectionMode ? (
              <>
                <CheckSquare className="w-4 h-4 mr-2" />
                {selectedShows.size} Selected
              </>
            ) : (
              <>
                <Square className="w-4 h-4 mr-2" />
                Select Shows
              </>
            )}
          </Button>

          {/* Select All / Clear buttons when in selection mode */}
          {selectionMode && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedShows(new Set(shows.map(s => s.id)))}
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedShows(new Set())}
              >
                Clear
              </Button>
            </>
          )}

          {/* Bulk Announce buttons when in selection mode */}
          {selectionMode && selectedShows.size > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => bulkToggleAnnounced(true)}
                className="text-green-600 border-green-600/50 hover:bg-green-600/10"
              >
                <Megaphone className="w-4 h-4 mr-1" />
                Announce
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => bulkToggleAnnounced(false)}
                className="text-orange-600 border-orange-600/50 hover:bg-orange-600/10"
              >
                <MegaphoneOff className="w-4 h-4 mr-1" />
                Unannounce
              </Button>
              
              {/* Bulk Performance Type Update */}
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    bulkUpdateShows({ performance_type: e.target.value });
                    e.target.value = '';
                  }
                }}
                className="h-8 px-2 bg-input/50 border border-border text-foreground text-xs rounded-lg focus:border-primary outline-none"
                defaultValue=""
              >
                <option value="" disabled>Set Type...</option>
                <option value="solo_live">Solo Live</option>
                <option value="live_band">Live Band</option>
                <option value="dj">DJ Set</option>
              </select>
              
              {/* Bulk Show Type Update */}
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    bulkUpdateShows({ show_type: e.target.value });
                    e.target.value = '';
                  }
                }}
                className="h-8 px-2 bg-input/50 border border-border text-foreground text-xs rounded-lg focus:border-primary outline-none"
                defaultValue=""
              >
                <option value="" disabled>Set Venue...</option>
                <option value="venue">Venue</option>
                <option value="festival">Festival</option>
                <option value="show">Show</option>
                <option value="tour">Tour</option>
              </select>
            </>
          )}

          {/* Divider */}
          {!selectionMode && <div className="w-px h-6 bg-border/50" />}

          {/* Announced Filter */}
          {!selectionMode && (
            <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1">
              <Button
                variant={announcedFilter === 'all' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setAnnouncedFilter('all')}
                className="h-7 px-2 text-xs"
              >
                All
              </Button>
              <Button
                variant={announcedFilter === 'announced' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setAnnouncedFilter('announced')}
                className="h-7 px-2 text-xs"
              >
                <Megaphone className="w-3 h-3 mr-1" />
                Announced ({announcedCount})
              </Button>
              <Button
                variant={announcedFilter === 'unannounced' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setAnnouncedFilter('unannounced')}
                className="h-7 px-2 text-xs"
              >
                <MegaphoneOff className="w-3 h-3 mr-1" />
                Unannounced ({unannouncedCount})
              </Button>
            </div>
          )}

          {/* Export CSV - respects selection or artist filter */}
          <Button
            variant="outline"
            onClick={() => {
              const token = localStorage.getItem('token');
              let downloadUrl = `${API}/export/shows/csv/download?token=${encodeURIComponent(token)}`;
              
              // If shows selected, export only those
              if (selectionMode && selectedShows.size > 0) {
                downloadUrl += `&show_ids=${Array.from(selectedShows).join(',')}`;
              } else if (selectedArtist && selectedArtist !== 'all') {
                // Otherwise respect artist filter
                downloadUrl += `&artist_id=${encodeURIComponent(selectedArtist)}`;
              }
              
              // Create a hidden link and click it - more reliable across browsers including Safari
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              toast.success('Opening CSV...');
            }}
            disabled={selectionMode && selectedShows.size === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            {selectionMode && selectedShows.size > 0 
              ? `Export ${selectedShows.size} Shows` 
              : 'Export CSV'}
          </Button>

          {/* Email Schedule */}
          <Button
            variant="outline"
            onClick={() => setEmailDialogOpen(true)}
            disabled={selectionMode ? selectedShows.size === 0 : upcomingShows.length === 0}
          >
            <Mail className="w-4 h-4 mr-2" />
            {selectionMode && selectedShows.size > 0 
              ? `Email ${selectedShows.size} Shows` 
              : 'Email Schedule'}
          </Button>
        </div>

        {/* Primary Actions Row */}
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
            className="flex items-center gap-2"
            data-testid="import-shows-button"
          >
            <Upload className="w-4 h-4" />
            Import Shows
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
                data-testid="add-show-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Show
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="font-manrope font-bold text-2xl">
                {editingShow ? 'Edit Live Show' : 'Add Live Show'}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto pr-2">
              <form onSubmit={handleSubmit} className="space-y-6 pb-4">
                <div className="space-y-4">
                <Label htmlFor="venue">Venue</Label>
                <Input
                  id="venue"
                  data-testid="show-venue-input"
                  value={formData.venue}
                  onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    data-testid="show-city-input"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    data-testid="show-country-input"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    required
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Show Date</Label>
                <Input
                  id="date"
                  type="datetime-local"
                  data-testid="show-date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              {user?.role === 'manager' || user?.role === 'admin' && (
                <div className="space-y-2">
                  <Label htmlFor="artist_id">Artist</Label>
                  <select
                    id="artist_id"
                    data-testid="show-artist-select"
                    value={formData.artist_id || selectedArtist || ''}
                    onChange={(e) => setFormData({ ...formData, artist_id: e.target.value })}
                    className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <option value="">Select artist (optional)</option>
                    {artists.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* New Financial Tracking Fields */}
              <div className="border-t border-border/50 pt-4">
                <h3 className="font-manrope font-semibold text-foreground mb-4">Show Details & Finances</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="show_type">Show Type</Label>
                      <select
                        id="show_type"
                        data-testid="show-type-select"
                        value={formData.show_type}
                        onChange={(e) => setFormData({ ...formData, show_type: e.target.value })}
                        className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      >
                        <option value="venue">Venue</option>
                        <option value="festival">Festival</option>
                        <option value="show">Show</option>
                        <option value="tour">Tour</option>
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="performance_type">Performance Type</Label>
                      <select
                        id="performance_type"
                        data-testid="performance-type-select"
                        value={formData.performance_type}
                        onChange={(e) => setFormData({ ...formData, performance_type: e.target.value })}
                        className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      >
                        {PERFORMANCE_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <select
                      id="currency"
                      data-testid="show-currency-select"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    >
                      {CURRENCIES.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fee">Fee ({getCurrencySymbol(formData.currency)})</Label>
                      <Input
                        id="fee"
                        type="number"
                        step="0.01"
                        data-testid="show-fee-input"
                        value={formData.fee}
                        onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                        className="bg-input/50 border-border rounded-xl"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="costs">Costs ({getCurrencySymbol(formData.currency)})</Label>
                      <Input
                        id="costs"
                        type="number"
                        step="0.01"
                        data-testid="show-costs-input"
                        value={formData.costs}
                        onChange={(e) => setFormData({ ...formData, costs: e.target.value })}
                        className="bg-input/50 border-border rounded-xl"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="tour_name">Tour Name (Optional)</Label>
                    <Input
                      id="tour_name"
                      data-testid="tour-name-input"
                      list="existing-tours"
                      value={formData.tour_name}
                      onChange={(e) => setFormData({ ...formData, tour_name: e.target.value })}
                      className="bg-input/50 border-border rounded-xl"
                      placeholder="e.g., Summer Tour 2024"
                    />
                    <datalist id="existing-tours">
                      {existingTours.map(tour => (
                        <option key={tour} value={tour} />
                      ))}
                    </datalist>
                    <p className="text-xs text-muted-foreground">Group shows together for tour financial tracking</p>
                  </div>

                  {/* Announced Status */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, announced: !formData.announced })}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-colors ${
                          formData.announced 
                            ? 'bg-green-500/20 border-green-500/50 text-green-600' 
                            : 'bg-muted/50 border-border text-muted-foreground'
                        }`}
                      >
                        {formData.announced ? <Megaphone className="w-4 h-4" /> : <MegaphoneOff className="w-4 h-4" />}
                        {formData.announced ? 'Announced' : 'Not Announced'}
                      </button>
                      <p className="text-xs text-muted-foreground flex-1">
                        Mark if this show has been publicly announced
                      </p>
                    </div>
                  </div>

                  {/* Receipt Uploads */}
                  <div className="space-y-2">
                    <Label htmlFor="receipts">Upload Cost Receipts (Optional)</Label>
                    <Input
                      id="receipts"
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleReceiptUpload}
                      className="bg-input/50 border-border rounded-xl"
                    />
                    {receiptFiles.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {receiptFiles.map((receipt, index) => (
                          <div key={receipt.filename || `receipt-${index}`} className="flex items-center justify-between bg-muted/50 p-2 rounded">
                            <span className="text-sm truncate">{receipt.filename}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeReceipt(index)}
                              className="h-6 px-2"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">Upload receipts to document show expenses</p>
                  </div>
                  
                  {formData.fee && (
                    <div className="bg-muted/50 p-4 rounded-xl space-y-2">
                      <h4 className="font-medium text-sm">Financial Breakdown:</h4>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span>Gross Fee:</span>
                          <span>{getCurrencySymbol(formData.currency)}{parseFloat(formData.fee || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-yellow-600">
                          <span>Agent Commission (10%):</span>
                          <span>-{getCurrencySymbol(formData.currency)}{(parseFloat(formData.fee || 0) * 0.10).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-red-600">
                          <span>Costs:</span>
                          <span>-{getCurrencySymbol(formData.currency)}{parseFloat(formData.costs || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t pt-1">
                          <span>Net Profit:</span>
                          <span className={(parseFloat(formData.fee || 0) - (parseFloat(formData.fee || 0) * 0.10) - parseFloat(formData.costs || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {getCurrencySymbol(formData.currency)}{(parseFloat(formData.fee || 0) - (parseFloat(formData.fee || 0) * 0.10) - parseFloat(formData.costs || 0)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="show-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-input/50 border-border rounded-xl min-h-24"
                />
              </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="submit-show-button"
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    {editingShow ? 'Update Show' : 'Add Show'}
                  </Button>
                </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>
          
          {/* Import Shows Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogContent className="bg-card border-border/50 max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="font-manrope font-bold text-2xl">Import Shows</DialogTitle>
              </DialogHeader>
              <ImportShowsContent 
                selectedArtist={selectedArtist}
                importingImage={importingImage}
                setImportingImage={setImportingImage}
                setImportDialogOpen={setImportDialogOpen}
                fetchShows={fetchShows}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Toggle Group by Tour */}
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setGroupByTour(!groupByTour)}
        >
          {groupByTour ? 'Show All' : 'Group by Tour'}
        </Button>
      </div>

      <div className="space-y-8">
        {shows.length === 0 ? (
          <Card className="bg-card border-border/50 p-12 text-center">
            <p className="text-muted-foreground">No shows scheduled yet. Add your first show!</p>
          </Card>
        ) : (
          <>
            {/* Upcoming Shows Section */}
            {upcomingShows.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-gradient-to-r from-primary/50 to-transparent"></div>
                  <h2 className="font-manrope font-bold text-2xl text-foreground flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></span>
                    Upcoming Shows ({upcomingShows.length})
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-l from-primary/50 to-transparent"></div>
                </div>
                <div className="grid gap-6">
                  {Object.entries(groupedUpcoming).map(([tourName, tourShows]) => {
            const isExpanded = expandedTours[tourName] !== false;
            const totals = getTourTotals(tourShows);
            
            return (
              <div key={tourName}>
                {groupByTour && (
                  <Card className="bg-card border-border/50 p-4 mb-3">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedTours(prev => ({ ...prev, [tourName]: !isExpanded }))}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                        <div>
                          <h3 className="font-semibold text-lg">{tourName}</h3>
                          <p className="text-sm text-muted-foreground">{totals.showCount} shows</p>
                        </div>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div className="text-right">
                          <p className="text-muted-foreground">Total Income</p>
                          <p className="font-semibold text-green-600">£{totals.totalFee.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-muted-foreground">Total Costs</p>
                          <p className="font-semibold text-red-600">£{totals.totalCosts.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-muted-foreground">Net Profit</p>
                          <p className={`font-semibold ${totals.netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                            £{totals.netProfit.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
                
                {isExpanded && tourShows.map((show) => (
            <Card
              key={show.id}
              className={`bg-card border-border/50 p-6 hover:border-primary/50 transition-colors ${selectionMode && selectedShows.has(show.id) ? 'ring-2 ring-primary border-primary' : ''}`}
              data-testid={`show-item-${show.id}`}
              onClick={selectionMode ? () => {
                const newSelected = new Set(selectedShows);
                if (newSelected.has(show.id)) {
                  newSelected.delete(show.id);
                } else {
                  newSelected.add(show.id);
                }
                setSelectedShows(newSelected);
              } : undefined}
              style={selectionMode ? { cursor: 'pointer' } : undefined}
            >
              <div className="flex flex-col">
                {/* Header with Selection checkbox and Edit button */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 flex items-start gap-3">
                    {/* Selection checkbox */}
                    {selectionMode && (
                      <div 
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center mt-3 ${
                          selectedShows.has(show.id) 
                            ? 'bg-primary border-primary' 
                            : 'border-muted-foreground/50 hover:border-primary'
                        }`}
                      >
                        {selectedShows.has(show.id) && <Check className="w-4 h-4 text-white" />}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-manrope font-bold text-xl text-foreground">{show.venue}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            show.show_type === 'festival' ? 'bg-purple-500/20 text-purple-500' :
                            show.show_type === 'tour' ? 'bg-blue-500/20 text-blue-500' :
                            show.show_type === 'show' ? 'bg-green-500/20 text-green-500' :
                            'bg-gray-500/20 text-gray-500'
                          }`}>
                            {show.show_type || 'venue'}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            show.performance_type === 'live_band' ? 'bg-pink-500/20 text-pink-500' :
                            show.performance_type === 'dj' ? 'bg-cyan-500/20 text-cyan-500' :
                            'bg-amber-500/20 text-amber-500'
                          }`}>
                            {show.performance_type === 'live_band' ? 'Live Band' :
                             show.performance_type === 'dj' ? 'DJ Set' : 'Solo Live'}
                          </span>
                          {show.tour_name && (
                            <span className="px-2 py-1 bg-orange-500/20 text-orange-500 rounded-full text-xs font-medium">
                              {show.tour_name}
                            </span>
                          )}
                          {/* Announced Badge */}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                            show.announced 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-muted-foreground/20 text-muted-foreground'
                          }`}>
                            {show.announced ? <Megaphone className="w-3 h-3" /> : <MegaphoneOff className="w-3 h-3" />}
                            {show.announced ? 'Announced' : 'Unannounced'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {(!selectedArtist || selectedArtist === 'all') && getArtistName(show.artist_id) && (
                            <span className="font-medium text-primary mr-2">{getArtistName(show.artist_id)} •</span>
                          )}
                          {show.city}, {show.country}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {/* Announce Toggle Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleAnnounced(show.id, show.announced);
                      }}
                      className={`min-w-[36px] min-h-[36px] touch-none ${
                        show.announced 
                          ? 'text-green-600 border-green-600/50 hover:bg-green-600/10' 
                          : 'text-muted-foreground'
                      }`}
                      title={show.announced ? 'Mark as unannounced' : 'Mark as announced'}
                    >
                      {show.announced ? <Megaphone className="w-4 h-4" /> : <MegaphoneOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(show);
                      }}
                      className="min-w-[70px] min-h-[36px] touch-none"
                      data-testid={`edit-show-${show.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-primary">
                    <span className="font-mono text-sm font-medium">
                      {new Date(show.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="font-mono text-sm">
                      {new Date(show.date).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  
                  {/* Financial Summary */}
                  {(show.fee !== null || show.costs !== null) && (
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <h4 className="font-medium text-sm">Financial Summary</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        {show.fee !== null && (
                          <div>
                            <span className="text-muted-foreground block">Gross Fee</span>
                            <span className="font-semibold text-green-600">
                              <CurrencyDisplay amount={show.fee} currency={show.currency || 'GBP'} baseCurrency={baseCurrency} getCurrencySymbol={getCurrencySymbol} convertCurrency={convertCurrency} />
                            </span>
                          </div>
                        )}
                        {show.agent_commission !== null && (
                          <div>
                            <span className="text-muted-foreground block">Agent (10%)</span>
                            <span className="font-medium text-yellow-600">
                              -<CurrencyDisplay amount={show.agent_commission} currency={show.currency || 'GBP'} baseCurrency={baseCurrency} getCurrencySymbol={getCurrencySymbol} convertCurrency={convertCurrency} />
                            </span>
                          </div>
                        )}
                        {show.costs !== null && (
                          <div>
                            <span className="text-muted-foreground block">Costs</span>
                            <span className="font-medium text-red-600">
                              -<CurrencyDisplay amount={show.costs} currency={show.currency || 'GBP'} baseCurrency={baseCurrency} getCurrencySymbol={getCurrencySymbol} convertCurrency={convertCurrency} />
                            </span>
                          </div>
                        )}
                        {show.net_profit !== null && (
                          <div>
                            <span className="text-muted-foreground block">Net Profit</span>
                            <span className={`font-bold ${show.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              <CurrencyDisplay amount={show.net_profit} currency={show.currency || 'GBP'} baseCurrency={baseCurrency} getCurrencySymbol={getCurrencySymbol} convertCurrency={convertCurrency} />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {show.notes && <p className="text-sm text-muted-foreground">{show.notes}</p>}
                  
                  {/* Guestlist Section */}
                  <div className="mt-3 p-3 rounded-xl bg-muted/30 border border-border/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary" />
                        <span className="font-medium text-sm">Guestlist</span>
                        {countGuests(show.guestlist) > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-medium">
                            {countGuests(show.guestlist)} guests
                          </span>
                        )}
                      </div>
                      {editingGuestlist !== show.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingGuestlist(show);
                          }}
                          className="h-7 px-2 text-xs"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                    
                    {editingGuestlist === show.id ? (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <textarea
                          value={guestlistText}
                          onChange={(e) => setGuestlistText(e.target.value)}
                          placeholder="Enter guest names (one per line)..."
                          className="w-full h-32 px-3 py-2 bg-input/50 border border-border text-foreground rounded-lg text-sm resize-none focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEditingGuestlist}
                            className="h-7 px-2 text-xs"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => saveGuestlist(show.id)}
                            className="h-7 px-3 text-xs bg-primary"
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {show.guestlist ? show.guestlist : 'No guests added yet'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Delete button - compact, right-aligned */}
                <div className="mt-4 pt-3 border-t border-border/30 flex justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => confirmDeleteShow(show)}
                    className="h-8 px-3 text-xs"
                    data-testid={`delete-show-${show.id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
              ))}
              </div>
            );
          })}
              </div>
            </div>
          )}

            {/* Completed/Past Shows Section */}
            {pastShows.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-gradient-to-r from-muted-foreground/30 to-transparent"></div>
                  <h2 className="font-manrope font-bold text-2xl text-muted-foreground flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-muted-foreground/50"></span>
                    Completed Shows ({pastShows.length})
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-l from-muted-foreground/30 to-transparent"></div>
                </div>
                <div className="grid gap-6">
                  {Object.entries(groupedPast).map(([tourName, tourShows]) => {
            const isExpanded = expandedTours[`past-${tourName}`] !== false;
            const totals = getTourTotals(tourShows);
            
            return (
              <div key={`past-${tourName}`}>
                {groupByTour && (
                  <Card className="bg-muted/30 border-muted-foreground/30 p-4 mb-3 opacity-70">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedTours(prev => ({ ...prev, [`past-${tourName}`]: !isExpanded }))}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                        <div>
                          <h3 className="font-semibold text-lg">{tourName}</h3>
                          <p className="text-sm text-muted-foreground">{totals.showCount} shows</p>
                        </div>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div className="text-right">
                          <p className="text-muted-foreground">Total Income</p>
                          <p className="font-semibold text-green-600">£{totals.totalFee.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-muted-foreground">Total Costs</p>
                          <p className="font-semibold text-red-600">£{totals.totalCosts.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-muted-foreground">Net Profit</p>
                          <p className={`font-semibold ${totals.netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                            £{totals.netProfit.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
                
                {isExpanded && tourShows.map((show) => (
            <Card
              key={show.id}
              className={`bg-muted/30 border-muted-foreground/30 p-6 hover:border-muted-foreground/50 transition-colors opacity-70 ${selectionMode && selectedShows.has(show.id) ? 'ring-2 ring-primary border-primary opacity-100' : ''}`}
              data-testid={`show-item-${show.id}`}
              onClick={selectionMode ? () => {
                const newSelected = new Set(selectedShows);
                if (newSelected.has(show.id)) {
                  newSelected.delete(show.id);
                } else {
                  newSelected.add(show.id);
                }
                setSelectedShows(newSelected);
              } : undefined}
              style={selectionMode ? { cursor: 'pointer' } : undefined}
            >
              <div className="flex flex-col">
                {/* Header with Selection checkbox and Edit button */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 flex items-start gap-3">
                    {/* Selection checkbox */}
                    {selectionMode && (
                      <div 
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center mt-3 ${
                          selectedShows.has(show.id) 
                            ? 'bg-primary border-primary' 
                            : 'border-muted-foreground/50 hover:border-primary'
                        }`}
                      >
                        {selectedShows.has(show.id) && <Check className="w-4 h-4 text-white" />}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-xl bg-muted-foreground/20 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-manrope font-bold text-xl text-muted-foreground">{show.venue}</h3>
                          <span className="px-2 py-1 bg-muted-foreground/20 text-muted-foreground rounded-full text-xs font-medium">
                            COMPLETED
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize opacity-60 ${
                            show.show_type === 'festival' ? 'bg-purple-500/20 text-purple-500' :
                            show.show_type === 'tour' ? 'bg-blue-500/20 text-blue-500' :
                            show.show_type === 'show' ? 'bg-green-500/20 text-green-500' :
                            'bg-gray-500/20 text-gray-500'
                          }`}>
                            {show.show_type || 'venue'}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium opacity-60 ${
                            show.performance_type === 'live_band' ? 'bg-pink-500/20 text-pink-500' :
                            show.performance_type === 'dj' ? 'bg-cyan-500/20 text-cyan-500' :
                            'bg-amber-500/20 text-amber-500'
                          }`}>
                            {show.performance_type === 'live_band' ? 'Live Band' :
                             show.performance_type === 'dj' ? 'DJ Set' : 'Solo Live'}
                          </span>
                          {show.tour_name && (
                            <span className="px-2 py-1 bg-muted-foreground/20 text-muted-foreground rounded-full text-xs font-medium opacity-60">
                              {show.tour_name}
                            </span>
                          )}
                          {/* Announced Badge (for past shows) */}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 opacity-60 ${
                            show.announced 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-muted-foreground/20 text-muted-foreground'
                          }`}>
                            {show.announced ? <Megaphone className="w-3 h-3" /> : <MegaphoneOff className="w-3 h-3" />}
                            {show.announced ? 'Announced' : 'Unannounced'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {show.city}, {show.country}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {/* Announce Toggle Button (for past shows) */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleAnnounced(show.id, show.announced);
                      }}
                      className={`min-w-[36px] min-h-[36px] touch-none opacity-60 ${
                        show.announced 
                          ? 'text-green-600 border-green-600/50 hover:bg-green-600/10' 
                          : 'text-muted-foreground'
                      }`}
                      title={show.announced ? 'Mark as unannounced' : 'Mark as announced'}
                    >
                      {show.announced ? <Megaphone className="w-4 h-4" /> : <MegaphoneOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(show);
                      }}
                      className="min-w-[70px] min-h-[36px] touch-none opacity-60"
                      data-testid={`edit-show-${show.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="font-mono text-sm font-medium">
                      {new Date(show.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="font-mono text-sm">
                      {new Date(show.date).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  
                  {/* Financial Summary */}
                  {(show.fee !== null || show.costs !== null) && (
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">Financial Summary</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        {show.fee !== null && (
                          <div>
                            <span className="text-muted-foreground block">Gross Fee</span>
                            <span className="font-semibold text-green-600 opacity-70">
                              <CurrencyDisplay amount={show.fee} currency={show.currency || 'GBP'} baseCurrency={baseCurrency} getCurrencySymbol={getCurrencySymbol} convertCurrency={convertCurrency} />
                            </span>
                          </div>
                        )}
                        {show.agent_commission !== null && (
                          <div>
                            <span className="text-muted-foreground block">Agent (10%)</span>
                            <span className="font-medium text-yellow-600 opacity-70">
                              -<CurrencyDisplay amount={show.agent_commission} currency={show.currency || 'GBP'} baseCurrency={baseCurrency} getCurrencySymbol={getCurrencySymbol} convertCurrency={convertCurrency} />
                            </span>
                          </div>
                        )}
                        {show.costs !== null && (
                          <div>
                            <span className="text-muted-foreground block">Costs</span>
                            <span className="font-medium text-red-600 opacity-70">
                              -<CurrencyDisplay amount={show.costs} currency={show.currency || 'GBP'} baseCurrency={baseCurrency} getCurrencySymbol={getCurrencySymbol} convertCurrency={convertCurrency} />
                            </span>
                          </div>
                        )}
                        {show.net_profit !== null && (
                          <div>
                            <span className="text-muted-foreground block">Net Profit</span>
                            <span className={`font-bold opacity-70 ${show.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              <CurrencyDisplay amount={show.net_profit} currency={show.currency || 'GBP'} baseCurrency={baseCurrency} getCurrencySymbol={getCurrencySymbol} convertCurrency={convertCurrency} />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Receipts Section */}
                  {show.receipts && show.receipts.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2 text-muted-foreground">Receipts ({show.receipts.length})</h4>
                      <div className="flex flex-wrap gap-2">
                        {show.receipts.map((receipt) => (
                          <Button
                            key={receipt.filename}
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = `data:${receipt.file_type};base64,${receipt.file_data}`;
                              link.download = receipt.filename;
                              link.click();
                            }}
                            className="opacity-60"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            {receipt.filename}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {show.notes && (
                    <p className="text-sm text-muted-foreground p-3 rounded-xl bg-muted/30">{show.notes}</p>
                  )}
                  
                  {/* Guestlist Section (Past Shows) */}
                  {show.guestlist && (
                    <div className="mt-3 p-3 rounded-xl bg-muted/30 border border-border/30 opacity-70">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium text-sm text-muted-foreground">Guestlist</span>
                        <span className="px-2 py-0.5 rounded-full bg-muted-foreground/20 text-muted-foreground text-xs font-medium">
                          {countGuests(show.guestlist)} guests
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {show.guestlist}
                      </div>
                    </div>
                  )}
                </div>

                {/* Delete button - compact, right-aligned */}
                <div className="flex justify-end mt-3 pt-3 border-t border-muted-foreground/20">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => confirmDeleteShow(show)}
                    className="h-7 px-2 text-xs opacity-70 hover:opacity-100"
                    data-testid={`delete-show-${show.id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
              </div>
            );
          })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Email Schedule Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-2xl">Email Show Schedule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEmailShows} className="space-y-4">
            <div className="p-4 rounded-xl bg-accent/20 border border-border/50">
              <p className="text-sm text-muted-foreground">
                {selectionMode && selectedShows.size > 0 
                  ? `Send ${selectedShows.size} selected show${selectedShows.size > 1 ? 's' : ''} to the recipient.`
                  : `Send a professional rundown of all upcoming shows (${upcomingShows.length} shows) to anyone.`
                }
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipient-name">Recipient Name</Label>
              <Input
                id="recipient-name"
                placeholder="e.g., John Smith"
                value={emailFormData.recipient_name}
                onChange={(e) => setEmailFormData({ ...emailFormData, recipient_name: e.target.value })}
                required
                className="bg-input/50 border-border rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipient-email">Recipient Email</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="e.g., booking@example.com"
                value={emailFormData.recipient_email}
                onChange={(e) => setEmailFormData({ ...emailFormData, recipient_email: e.target.value })}
                required
                className="bg-input/50 border-border rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-message">Message (optional)</Label>
              <Textarea
                id="email-message"
                placeholder="Add a personal message to include in the email..."
                value={emailFormData.message}
                onChange={(e) => setEmailFormData({ ...emailFormData, message: e.target.value })}
                className="bg-input/50 border-border rounded-xl min-h-24"
              />
            </div>

            {/* Toggle options for what to include */}
            <div className="p-3 rounded-lg bg-muted/30 space-y-3">
              <p className="text-xs font-medium text-muted-foreground">Include in email:</p>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!emailHidePerformance}
                    onChange={(e) => setEmailHidePerformance(!e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm">Performance Type</span>
                  <span className="text-xs text-muted-foreground">(Solo Live, Live Band, DJ)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!emailHideTour}
                    onChange={(e) => setEmailHideTour(!e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm">Tour Name</span>
                  <span className="text-xs text-muted-foreground">(e.g., Spring 2026 Tour)</span>
                </label>
              </div>
            </div>

            {/* Previous Shows Section */}
            <div className="p-3 rounded-lg bg-muted/30 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailIncludePreviousShows}
                  onChange={(e) => setEmailIncludePreviousShows(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                  data-testid="include-previous-shows-checkbox"
                />
                <span className="text-sm font-medium">Include Previous Shows Played</span>
              </label>
              {emailIncludePreviousShows && (
                <div className="flex items-center gap-2 ml-6">
                  <span className="text-sm text-muted-foreground">Show last</span>
                  <select
                    value={emailPreviousShowsCount}
                    onChange={(e) => setEmailPreviousShowsCount(parseInt(e.target.value))}
                    className="h-8 px-2 bg-input/50 border border-border text-foreground rounded-lg text-sm"
                    data-testid="previous-shows-count-select"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-muted-foreground">previous shows</span>
                </div>
              )}
            </div>

            <div className="p-3 rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">
                The email will include a formatted list of shows with dates, venues, cities, and countries.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEmailDialogOpen(false);
                  setEmailFormData({ recipient_email: '', recipient_name: '', message: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendingEmail}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setShowToDelete(null);
        }}
        onConfirm={deleteShow}
        title="Delete Show"
        itemName={showToDelete ? `${showToDelete.venue} - ${new Date(showToDelete.date).toLocaleDateString()}` : null}
        description="Are you sure you want to delete this show? This action cannot be undone."
      />
    </div>
  );
};

export default LiveShows;